'use client';

import { useState } from 'react';

import { Icon, SlidingTab, WinnerTicket } from '@/components';
import { Skeleton } from '@/components/ui';
import type { LuckyTicketsByRaffle } from '@/types/models';

type LuckySequenceSliderProps = {
  foundLuckySeq: LuckyTicketsByRaffle['data'];
  notFoundLuckySeq: LuckyTicketsByRaffle['data'];
  totalFoundLuckySeq: number;
  totalNotFoundLuckySeq: number;
  foundPageCount: number;
  notFoundPageCount: number;
  handlePaginationChange: (page: number, type: string) => void;
  showAvailable: boolean;
  isPaginating: boolean;
};

export const LuckySequenceSlider = ({
  foundLuckySeq,
  notFoundLuckySeq,
  totalFoundLuckySeq,
  totalNotFoundLuckySeq,
  foundPageCount,
  notFoundPageCount,
  handlePaginationChange,
  showAvailable,
  isPaginating,
}: LuckySequenceSliderProps) => {
  const [currentFoundPage, setCurrentFoundPage] = useState(1);
  const [currentNotFoundPage, setCurrentNotFoundPage] = useState(1);

  const SequenceFound = ({
    sequence,
  }: {
    sequence: LuckyTicketsByRaffle['data'][0];
    index: number;
  }) => {
    const formatValue = (value: number) => {
      return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    return (
      <div className="w-full max-h-28 md:h-32">
        <div className="flex flex-col justify-between p-2 bg-black/20 w-full h-16 rounded-xl">
          <div className="text-sm md:text-[16px] font-bold text-center text-foreground/50">
            {sequence.userName.toUpperCase()}
          </div>
          <div className="flex items-center justify-between w-full mt-0.5 md:px-2">
            <div className="flex items-center gap-1">
              <Icon.UserAccount width={14} height={11} className="fill-white/50" />
              <span className="text-xs text-foreground/50">{sequence.userCpf}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-foreground/50">{sequence.userPhone}</span>
            </div>
          </div>
        </div>

        <div className="h-0.5 border border-foreground/5 border-dashed mx-2.5"></div>

        <div className="flex justify-between p-2 bg-black/20 w-full h-14 md:h-16 rounded-xl">
          <div className="flex flex-col items-center w-1/2">
            <div className="text-sm text-foreground/50 font-bold">GANHOU:</div>
            <div className="font-bold text-base text-foreground/50 truncate text-center">
              R$ {formatValue(sequence.value)}
            </div>
          </div>
          <div className="flex items-center w-1/2">
            <WinnerTicket value={sequence.sequence} className="w-full h-8" />
          </div>
        </div>
      </div>
    );
  };

  const SequenceNotFound = ({
    sequence,
  }: {
    sequence: LuckyTicketsByRaffle['data'][0];
  }) => {
    const formatValue = (value: number) => {
      return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    return (
      <div className="flex h-8 gap-2 bg-foreground/15 border-dotted border-2 !border-foreground/25 rounded-lg p-2 items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <div className="text-base font-bold text-foreground text-center whitespace-break-spaces line-clamp-2">
            R$ {formatValue(sequence.value)}
          </div>
        </div>
      </div>
    );
  };

  const renderFoundSequences = () => (
    <div className="flex flex-col items-center w-full pb-4 px-6">
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4 lg:grid-cols-3 xl:grid-cols-4 md:gap-y-6">
        {foundLuckySeq
          ?.sort((a, b) => b.value - a.value)
          .map((sequence, index) => (
            <SequenceFound
              key={`${sequence.sequence}-${index}`}
              sequence={sequence}
              index={index}
            />
          ))}
      </div>
    </div>
  );

  const renderFoundSkeleton = () => (
    <div className="flex flex-col items-center w-full pb-4 px-6">
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4 lg:grid-cols-3 xl:grid-cols-4 md:gap-y-6">
        {Array.from({ length: 12 }).map((_, idx) => (
          <div key={idx} className="w-full">
            <Skeleton className="h-28 md:h-32 w-full rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderNotFoundSequences = () => (
    <div className="flex flex-col pt-6 w-full">
      <div className="grid grid-cols-3 gap-2 mb-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {notFoundLuckySeq
          ?.sort((a, b) => b.value - a.value)
          .map((sequence, index) => (
            <SequenceNotFound key={`${sequence.sequence}-${index}`} sequence={sequence} />
          ))}
      </div>
    </div>
  );

  const renderNotFoundSkeleton = () => (
    <div className="flex flex-col pt-6 w-full">
      <div className="grid grid-cols-3 gap-2 mb-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 24 }).map((_, idx) => (
          <Skeleton
            key={idx}
            className="h-8 w-full rounded-lg border-dotted border-2 !border-foreground/25 bg-foreground/15"
          />
        ))}
      </div>
    </div>
  );

  const tabs = [
    {
      tabLabel: `Encontrados (${totalFoundLuckySeq})`,
      tabValue: 'found',
      tabBody: isPaginating ? renderFoundSkeleton() : renderFoundSequences(),
    },
  ];

  if (showAvailable) {
    tabs.unshift({
      tabLabel: `Disponíveis (${totalNotFoundLuckySeq})`,
      tabValue: 'notFound',
      tabBody: isPaginating ? renderNotFoundSkeleton() : renderNotFoundSequences(),
    });
  }

  const [activeTab, setActiveTab] = useState<string>(
    showAvailable ? 'notFound' : 'found',
  );

  const currentPage = activeTab === 'found' ? currentFoundPage : currentNotFoundPage;
  const totalPages = activeTab === 'found' ? foundPageCount : notFoundPageCount;

  const handlePageChange = (page: number) => {
    if (activeTab === 'found') {
      setCurrentFoundPage(page);
      handlePaginationChange(page, 'found');
    } else {
      setCurrentNotFoundPage(page);
      handlePaginationChange(page, 'notFound');
    }
  };

  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  return (
    <div className="w-full">
      <SlidingTab
        tabs={tabs}
        defaultTab={showAvailable ? 'notFound' : 'found'}
        onTabChange={handleTabChange}
        pagination={
          totalPages > 1
            ? {
                currentPage,
                totalPages,
                onPageChange: handlePageChange,
                maxVisible: 5,
                loading: isPaginating,
              }
            : undefined
        }
        paginationMessage="Prêmios em dinheiro, representam o valor líquido da premiação."
      />
    </div>
  );
};
